import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useMyLeaveBalances } from '@/hooks/leave/useLeaveBalances';
import { Calendar, TrendingUp, AlertCircle, Info } from 'lucide-react';
import { getLeaveTypeLabel } from '@/constants/leaveTypes';

interface LeaveBalanceSummaryProps {
  className?: string;
  showProjections?: boolean;
}

export function LeaveBalanceSummary({ className = "", showProjections = false }: LeaveBalanceSummaryProps) {
  const { data: balances, isLoading } = useMyLeaveBalances();

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Leave Balances
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="space-y-2">
                <div className="h-4 bg-muted animate-pulse rounded" />
                <div className="h-2 bg-muted animate-pulse rounded" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!balances?.length) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Leave Balances
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Info className="h-4 w-4" />
            <span>No leave policies assigned</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          Leave Balances
          {showProjections && (
            <Badge variant="secondary" className="ml-2">
              <TrendingUp className="h-3 w-3 mr-1" />
              Live
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {balances.map((balance) => (
            <div key={balance.policy.leave_type} className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium">{getLeaveTypeLabel(balance.policy.leave_type)}</h4>
                    {balance.current_balance_days < 2 && balance.current_balance_days >= 0 && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <AlertCircle className="h-4 w-4 text-warning" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Low balance - consider planning ahead</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                    {balance.current_balance_days < 0 && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <Badge variant="destructive" className="text-xs">
                              Overdrawn
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Balance is negative - contact your manager</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {balance.current_balance_days} days available
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold">
                    {balance.current_balance_days}
                  </div>
                  <div className="text-xs text-muted-foreground">days</div>
                </div>
              </div>

              <div className="space-y-2">
                <Progress 
                  value={Math.max(0, (balance.current_balance_days / Math.max(balance.current_balance_days + 10, 1)) * 100)} 
                  className="h-2"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Available: {balance.current_balance_days} days</span>
                  <span>Policy: {balance.policy.name}</span>
                </div>
              </div>

              {showProjections && (
                <div className="p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2 text-sm">
                    <TrendingUp className="h-4 w-4 text-primary" />
                    <span className="font-medium">Current Balance</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Balance is calculated in real-time
                  </p>
                  <p className="text-sm font-medium mt-1">
                    Available: {balance.current_balance_days} days
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}